import { type EcosystemLocale, FALLBACK_LOCALE, isEcosystemLocale } from "@iec-id/shared";
import { SearchSelect } from "@iec-id/ui";
import { useForm } from "@tanstack/react-form";
import { i18n } from "@/lib/i18n";
import { useLocale } from "@/lib/locale";
import { useOnboardingT } from "../onboarding-i18n";
import { detectTimezone, languageOptions, timezoneOptions } from "../options";
import { StepTitle } from "../step-title";
import { StepForm, type StepProps } from "./step-shell";

/**
 * Шаг 3 — язык интерфейса и часовой пояс (макет 4b-3).
 *
 * Язык переключается сразу при выборе, а не после отправки шага: человек
 * видит, что выбрал, ещё до «Продолжить». На сервер значение уходит
 * отдельным шагом — переключение интерфейса ничего не сохраняет.
 */
/** Черновик и браузер дают строку; форма ждёт локаль из реестра (Д11: незнакомое → en). */
function pickEcosystemLocale(value: string): EcosystemLocale {
	return isEcosystemLocale(value) ? value : FALLBACK_LOCALE;
}

export function LocaleStep({ state, submit, pending, error }: StepProps) {
	const t = useOnboardingT();
	const locale = useLocale();
	const { draft } = state;

	// Черновик приезжает из БД, а у профиля есть СВОИ умолчания — `locale = en`
	// и `timezone = Europe/Prague`. Поэтому `draft.locale ?? locale` до правой
	// части не доходило никогда: человек с русским браузером из Москвы видел
	// English и Прагу и должен был исправлять оба поля руками. Пока шаг не
	// отправляли, в БД лежит не выбор человека, а умолчание таблицы, — и
	// показывать надо то, что о человеке известно из браузера.
	const visited = state.completedSteps.includes("locale");

	const form = useForm({
		defaultValues: {
			locale: pickEcosystemLocale(visited ? (draft.locale ?? locale) : locale),
			timezone: (visited ? draft.timezone : null) ?? detectTimezone(),
		},
		onSubmit: ({ value }) => {
			submit({ step: "locale", locale: value.locale, timezone: value.timezone });
		},
	});

	return (
		<>
			<StepTitle>{t("localeStep.title")}</StepTitle>

			<StepForm onSubmit={() => void form.handleSubmit()} pending={pending} error={error}>
				<form.Field name="locale">
					{(field) => (
						<SearchSelect
							label={t("localeStep.language")}
							name="locale"
							placeholder={t("localeStep.languagePlaceholder")}
							searchLabel={t("search.language")}
							emptyLabel={t("search.empty")}
							refineLabel={t("search.refine")}
							options={languageOptions()}
							value={field.state.value}
							disabled={pending}
							onValueChange={(next) => {
								field.handleChange(pickEcosystemLocale(next));
								void i18n.changeLanguage(next);
							}}
						/>
					)}
				</form.Field>

				<form.Field name="timezone">
					{(field) => (
						<SearchSelect
							label={t("localeStep.timezone")}
							name="timezone"
							placeholder={t("localeStep.timezonePlaceholder")}
							searchLabel={t("search.timezone")}
							emptyLabel={t("search.empty")}
							refineLabel={t("search.refine")}
							options={timezoneOptions(locale)}
							value={field.state.value}
							disabled={pending}
							onValueChange={field.handleChange}
						/>
					)}
				</form.Field>
			</StepForm>
		</>
	);
}
